import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, CreditCard, Loader2, ArrowUpRight } from "lucide-react";
import { getAvailability, createBooking, createCheckout, getConfig } from "../lib/api";
import { Reveal } from "./Reveal";

const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

export const BookingSection = () => {
    const [services, setServices] = useState([]);
    const [date, setDate] = useState(null);
    const [slots, setSlots] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [slot, setSlot] = useState(null);
    const [form, setForm] = useState(() => {
        const pre = new URLSearchParams(window.location.search).get("book");
        return { name: "", email: "", phone: "", service: pre || "", notes: "" };
    });
    const [submitting, setSubmitting] = useState(false);
    const [booking, setBooking] = useState(null);
    const [paying, setPaying] = useState(false);
    const [payChoice, setPayChoice] = useState("prepay");
    const [location, setLocation] = useState("virtual");
    const total = payChoice === "prepay" ? 95 : 100;

    useEffect(() => {
        getConfig().then((c) => setServices(c.services)).catch(() => {});
    }, []);

    useEffect(() => {
        const onPick = (e) => setForm((f) => ({ ...f, service: e.detail }));
        window.addEventListener("cw:book-service", onPick);
        return () => window.removeEventListener("cw:book-service", onPick);
    }, []);

    useEffect(() => {
        if (!date) return;
        setLoadingSlots(true);
        setSlot(null);
        getAvailability(format(date, "yyyy-MM-dd"))
            .then((r) => setSlots(r.slots))
            .catch(() => {
                setSlots([]);
                toast.error("Could not load availability. Please try again.");
            })
            .finally(() => setLoadingSlots(false));
    }, [date]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const ready = date && slot && form.name && form.email && form.service;
    const isFree = ["web-dev", "crm-dev"].includes(form.service);

    const requestCustom = () => {
        window.dispatchEvent(
            new CustomEvent("cw:prefill-contact", {
                detail: {
                    message:
                        "Hi Alex — I'd like to request a custom appointment during business hours. My preferred date and time: ",
                },
            })
        );
        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!ready) return;
        setSubmitting(true);
        try {
            const b = await createBooking({
                ...form,
                date: format(date, "yyyy-MM-dd"),
                time: slot,
                payment_choice: payChoice,
                location,
            });
            if (payChoice === "prepay" && !isFree) {
                const r = await createCheckout({
                    lookup_key: "consulting_prepay",
                    quantity: 1,
                    origin_url: window.location.origin,
                    booking_id: b.booking_id,
                });
                toast.success("Slot held — complete your $95 payment to confirm.");
                window.location.href = r.checkout_url;
                return;
            }
            setBooking(b);
            toast.success("Booking confirmed — check your email.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const payByCard = async () => {
        setPaying(true);
        try {
            const r = await createCheckout({
                lookup_key: "consulting_prepay",
                quantity: 1,
                origin_url: window.location.origin,
                booking_id: booking.booking_id,
            });
            window.location.href = r.checkout_url;
        } catch (err) {
            toast.error("Could not start card payment. You can also pay after your session.");
            setPaying(false);
        }
    };

    const reset = () => {
        setBooking(null);
        setDate(null);
        setSlots(null);
        setSlot(null);
        setForm({ name: "", email: "", phone: "", service: "", notes: "" });
    };

    return (
        <section id="booking" data-testid="booking-section" className="py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-10">
                <Reveal>
                    <p className="flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-ink/50">
                        <span className="h-px w-10 bg-worx inline-block" /> Booking
                    </p>
                    <h2 className="mt-6 font-display font-semibold tracking-tighter leading-[0.95] text-5xl md:text-7xl">
                        Grab a time that works.
                    </h2>
                    <p className="mt-6 text-ink/60 max-w-lg text-base md:text-lg">
                        Every website, app, or CRM project starts with a free one-hour discovery call —
                        virtual on Google Meet or in person. One-on-one AI classes are $95 prepaid or $100
                        after class. Regular hours are Monday–Thursday evenings and Saturday daytime —
                        custom appointments during business hours are available upon request, just ask
                        through the contact form below.
                    </p>
                    <button
                        type="button"
                        data-testid="custom-request-btn"
                        onClick={requestCustom}
                        className="mt-6 inline-flex items-center gap-2 border border-ink px-5 py-2.5 text-sm font-medium hover:bg-ink hover:text-paper transition-colors duration-200"
                    >
                        Request a custom appointment <ArrowUpRight size={14} />
                    </button>
                </Reveal>

                {booking ? (
                    <Reveal className="mt-14">
                        <div data-testid="booking-confirmation" className="border border-ink/15 bg-white p-8 md:p-12 max-w-2xl">
                            <CheckCircle2 size={40} className="text-worx" />
                            <h3 className="mt-4 font-display font-semibold text-3xl tracking-tight">You're booked.</h3>
                            <p className="mt-2 text-ink/60">
                                A confirmation email is on its way to <strong>{booking.email}</strong>.
                            </p>
                            <dl className="mt-6 grid grid-cols-2 gap-y-3 text-sm">
                                <dt className="text-ink/50">Reference</dt>
                                <dd data-testid="booking-ref" className="font-mono">{booking.booking_id.slice(0, 8).toUpperCase()}</dd>
                                <dt className="text-ink/50">Service</dt><dd>{booking.service_name}</dd>
                                <dt className="text-ink/50">Date</dt><dd>{booking.date}</dd>
                                <dt className="text-ink/50">Time</dt><dd>{fmtTime(booking.time)}</dd>
                                <dt className="text-ink/50">Location</dt>
                                <dd data-testid="booking-location">{booking.location === "in-person" ? "In person" : "Virtual — Google Meet"}</dd>
                                {booking.location === "virtual" && (
                                    <>
                                        <dt className="text-ink/50">Meet link</dt>
                                        <dd className="text-ink/60">Arrives by Google Calendar invite email</dd>
                                    </>
                                )}
                                <dt className="text-ink/50">Duration</dt><dd>1 hour</dd>
                                <dt className="text-ink/50">Total</dt>
                                <dd data-testid="booking-total" className="font-semibold">${booking.price.toFixed(2)}</dd>
                            </dl>
                            <div className="mt-8 flex flex-wrap gap-3">
                                {booking.price > 0 && (
                                <Button
                                    data-testid="pay-by-card-btn"
                                    onClick={payByCard}
                                    disabled={paying}
                                    className="bg-worx hover:bg-ink text-white rounded-none px-6 py-6"
                                >
                                    {paying ? <Loader2 className="animate-spin mr-2" size={16} /> : <CreditCard className="mr-2" size={16} />}
                                    Prepay $95 by card — save $5
                                </Button>
                                )}
                                <Button
                                    data-testid="book-another-btn"
                                    variant="outline"
                                    onClick={reset}
                                    className="rounded-none border-ink px-6 py-6"
                                >
                                    Book another session
                                </Button>
                                <a
                                    href={`/manage/${booking.booking_id}`}
                                    data-testid="booking-manage-link"
                                    className="inline-flex items-center px-2 py-2 text-sm text-ink/60 hover:text-worx transition-colors duration-200"
                                >
                                    Change or cancel this booking
                                </a>
                            </div>
                            {booking.price > 0 && (
                            <p className="mt-4 text-sm text-ink/50">
                                Prefer to pay after class? CashApp and cash work too.
                            </p>
                            )}
                        </div>
                    </Reveal>
                ) : (
                    <Reveal className="mt-14">
                        <div className="grid lg:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                            <div className="bg-white p-6 md:p-10">
                                <h3 className="font-display font-medium text-xl flex items-center gap-2">
                                    <CalendarDays size={18} className="text-worx" /> 1. Pick a day
                                </h3>
                                <div className="mt-6 flex justify-center">
                                    <Calendar
                                        data-testid="booking-calendar"
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={[{ before: new Date() }]}
                                        className="border border-ink/10"
                                    />
                                </div>
                                {date && (
                                    <div className="mt-8">
                                        <h4 className="font-display font-medium text-lg">2. Pick a time — {format(date, "EEEE, MMM d")}</h4>
                                        {loadingSlots ? (
                                            <div data-testid="slots-loading" className="mt-4 flex items-center gap-2 text-ink/50">
                                                <Loader2 className="animate-spin" size={16} /> Checking availability…
                                            </div>
                                        ) : slots && slots.length > 0 ? (
                                            <div data-testid="slots-grid" className="mt-4 grid grid-cols-3 gap-2">
                                                {slots.map((s) => (
                                                    <button
                                                        key={s.time}
                                                        data-testid={`slot-${s.time}`}
                                                        disabled={!s.available}
                                                        onClick={() => setSlot(s.time)}
                                                        className={`py-2.5 text-sm border transition-colors duration-150 ${
                                                            slot === s.time
                                                                ? "bg-worx text-white border-worx"
                                                                : s.available
                                                                    ? "border-ink/15 hover:border-ink"
                                                                    : "border-ink/5 text-ink/25 line-through cursor-not-allowed"
                                                        }`}
                                                    >
                                                        {fmtTime(s.time)}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p data-testid="slots-empty" className="mt-4 text-ink/50">No slots this day — try another one.</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <form onSubmit={submit} className="bg-white p-6 md:p-10" data-testid="booking-form">
                                <h3 className="font-display font-medium text-xl">3. Your details</h3>
                                <div className="mt-6 space-y-5">
                                    <div>
                                        <Label htmlFor="bk-name">Name *</Label>
                                        <Input id="bk-name" data-testid="booking-name-input" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-none" />
                                    </div>
                                    <div>
                                        <Label htmlFor="bk-email">Email *</Label>
                                        <Input id="bk-email" data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-none" />
                                    </div>
                                    <div>
                                        <Label htmlFor="bk-phone">Phone</Label>
                                        <Input id="bk-phone" data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-none" />
                                    </div>
                                    <div>
                                        <Label>What are we working on? *</Label>
                                        {isFree && (
                                            <span data-testid="free-call-pill" className="ml-2 inline-block bg-worx text-white text-xs px-2 py-0.5 align-middle">
                                                Complimentary — free discovery call
                                            </span>
                                        )}
                                        <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                                            <SelectTrigger data-testid="booking-service-select" className="mt-1.5 rounded-none">
                                                <SelectValue placeholder="Choose a topic" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {services.map((s) => (
                                                    <SelectItem key={s.id} value={s.id} data-testid={`service-option-${s.id}`}>
                                                        {s.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor="bk-notes">Anything else?</Label>
                                        <Textarea id="bk-notes" data-testid="booking-notes-input" value={form.notes} onChange={set("notes")} rows={3} className="mt-1.5 rounded-none" placeholder="Goals, current tools, where you'd like to meet…" />
                                    </div>
                                    <div>
                                        <Label>Where do we meet?</Label>
                                        <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                data-testid="location-virtual"
                                                onClick={() => setLocation("virtual")}
                                                className={`p-4 text-left border transition-colors duration-150 ${location === "virtual" ? "border-worx bg-worx/5" : "border-ink/15 hover:border-ink"}`}
                                            >
                                                <span className="font-medium text-sm">Virtual — Google Meet</span>
                                                <span className="block text-xs text-ink/60 mt-1">Meet link emailed automatically when you book</span>
                                            </button>
                                            <button
                                                type="button"
                                                data-testid="location-in-person"
                                                onClick={() => setLocation("in-person")}
                                                className={`p-4 text-left border transition-colors duration-150 ${location === "in-person" ? "border-worx bg-worx/5" : "border-ink/15 hover:border-ink"}`}
                                            >
                                                <span className="font-medium text-sm">In person</span>
                                                <span className="block text-xs text-ink/60 mt-1">Your place or the Palm Beach County Main Library</span>
                                            </button>
                                        </div>
                                    </div>
                                    {!isFree && (
                                        <div>
                                            <Label>Payment</Label>
                                        <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                data-testid="pay-choice-prepay"
                                                onClick={() => setPayChoice("prepay")}
                                                className={`p-4 text-left border transition-colors duration-150 ${payChoice === "prepay" ? "border-worx bg-worx/5" : "border-ink/15 hover:border-ink"}`}
                                            >
                                                <span className="font-display font-semibold text-xl">$95</span>
                                                <span className="block text-xs text-ink/60 mt-1">Prepay online by card</span>
                                                <span className="inline-block mt-1.5 text-xs bg-worx text-white px-2 py-0.5">Save $5</span>
                                            </button>
                                            <button
                                                type="button"
                                                data-testid="pay-choice-later"
                                                onClick={() => setPayChoice("later")}
                                                className={`p-4 text-left border transition-colors duration-150 ${payChoice === "later" ? "border-worx bg-worx/5" : "border-ink/15 hover:border-ink"}`}
                                            >
                                                <span className="font-display font-semibold text-xl">$100</span>
                                                <span className="block text-xs text-ink/60 mt-1">After class — card, CashApp, or cash</span>
                                            </button>
                                        </div>
                                        </div>
                                    )}
                                    {isFree && (
                                        <p className="text-sm text-ink/60 bg-worx/5 border border-worx/20 p-4" data-testid="free-call-note">
                                            Discovery calls for website, app, and CRM projects are always free — we'll scope your build together, then you get a straight quote.
                                        </p>
                                    )}
                                    <div className="pt-2 flex items-center justify-between border-t border-ink/10 mt-2 pt-5">
                                        <span className="text-ink/60 text-sm">Total — 1 hour</span>
                                        <span data-testid="booking-form-total" className="font-display font-semibold text-2xl">{isFree ? "Free" : `$${total}.00`}</span>
                                    </div>
                                    <Button
                                        data-testid="booking-submit-btn"
                                        type="submit"
                                        disabled={!ready || submitting}
                                        className="w-full bg-worx hover:bg-ink text-white rounded-none py-6 text-base transition-colors duration-200"
                                    >
                                        {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                                        {ready ? (isFree ? "Confirm free discovery call" : "Confirm booking") : "Pick a day, time & topic first"}
                                    </Button>
                                    {!isFree && (
                                        <p className="text-xs text-ink/40 text-center">Pay now by card, or after your session via CashApp or cash.</p>
                                    )}
                                    <p className="text-xs text-ink/40 text-center" data-testid="booking-policy-note">Free cancellation up to 24 hours ahead — $25 fee for late cancellations and no-shows.</p>
                                </div>
                            </form>
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    );
};
